/**
 * Page-engagement mechanics — scroll depth and active dwell time.
 *
 * Framework-free on purpose: `packages/shared` carries no React dependency, so
 * this holds only the arithmetic and state-keeping. Each app supplies its own
 * mount (a `useEffect`) and its own emit — the marketing site publishes
 * `scroll_depth` / `time_on_page` through its `track()` helper, while the v2
 * signup shell publishes catalog events through `@onark/observability`.
 *
 * One implementation, two callers: the "did we cross a milestone yet" logic is
 * fiddly (visibility changes, short pages, SPA route resets) and is exactly the
 * kind of thing that rots when it's copy-pasted per app.
 */

/** Scroll-depth milestones, in percent of scrollable height. */
export const SCROLL_MILESTONES = [25, 50, 75, 100] as const;

/** Active-foreground dwell milestones, in milliseconds. */
export const DWELL_MILESTONES_MS = [30_000, 60_000, 180_000] as const;

export interface ScrollGeometry {
  /** `window.scrollY` */
  scrollY: number;
  /** `document.documentElement.scrollHeight` */
  scrollHeight: number;
  /** `window.innerHeight` */
  innerHeight: number;
}

/**
 * How far down the scrollable area the viewport currently sits, 0–100.
 *
 * Returns null when there is nothing to scroll (page shorter than the
 * viewport). A non-scrollable page hasn't "reached 100%" in any meaningful
 * sense, and treating it as 100 would report phantom deep reads on short
 * pages — the caller decides what to do with that.
 */
export function scrollPercent({
  innerHeight,
  scrollHeight,
  scrollY,
}: ScrollGeometry): number | null {
  const scrollable = scrollHeight - innerHeight;

  if (scrollable <= 0) return null;

  return (scrollY / scrollable) * 100;
}

/**
 * Tracks which scroll milestones have been crossed, so each fires exactly once
 * per instance. Build a fresh one per route (SPA navigation resets depth).
 */
export class ScrollDepthTracker {
  private readonly fired = new Set<number>();

  constructor(
    private readonly milestones: readonly number[] = SCROLL_MILESTONES
  ) {}

  /** Milestones newly crossed by this geometry — [] when nothing is new. */
  advance(geometry: ScrollGeometry): number[] {
    const pct = scrollPercent(geometry);

    if (pct === null) return [];

    const reached: number[] = [];

    for (const milestone of this.milestones) {
      if (pct >= milestone && !this.fired.has(milestone)) {
        this.fired.add(milestone);
        reached.push(milestone);
      }
    }

    return reached;
  }
}

/**
 * Accumulates ACTIVE foreground time and reports newly-crossed milestones.
 *
 * Background time doesn't count: a tab left open in another window for an hour
 * is not a sixty-minute read, and counting it would make dwell useless as an
 * attention signal. The caller drives this from a timer plus a
 * `visibilitychange` listener, and passes the clock in so it stays testable
 * without fake timers.
 */
export class DwellTracker {
  private readonly fired = new Set<number>();
  private elapsedMs = 0;
  private lastTickMs: number;
  private active: boolean;

  constructor(
    nowMs: number,
    startActive = true,
    private readonly milestones: readonly number[] = DWELL_MILESTONES_MS
  ) {
    this.lastTickMs = nowMs;
    this.active = startActive;
  }

  /** Fold elapsed time up to `nowMs`; returns milestones newly crossed. */
  tick(nowMs: number): number[] {
    if (this.active) this.elapsedMs += nowMs - this.lastTickMs;
    this.lastTickMs = nowMs;

    const reached: number[] = [];

    for (const milestone of this.milestones) {
      if (this.elapsedMs >= milestone && !this.fired.has(milestone)) {
        this.fired.add(milestone);
        reached.push(milestone);
      }
    }

    return reached;
  }

  /**
   * Foreground/background flip. Banks the time accrued so far before changing
   * state, so the segment that just ended is counted at its true length.
   */
  setActive(nowMs: number, active: boolean): void {
    if (this.active) this.elapsedMs += nowMs - this.lastTickMs;
    this.lastTickMs = nowMs;
    this.active = active;
  }

  /** Active foreground milliseconds banked so far. */
  get activeMs(): number {
    return this.elapsedMs;
  }
}
